import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Megaphone, 
  Plus, 
  Edit2, 
  Trash2, 
  Gift, 
  Trophy, 
  AlertTriangle,
  Calendar,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const ANNOUNCEMENT_TYPES = [
  { value: 'offer', label: 'Offer', icon: Gift, color: 'emerald', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700', borderColor: 'border-emerald-300' },
  { value: 'tournament', label: 'Tournament', icon: Trophy, color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-700', borderColor: 'border-blue-300' },
  { value: 'alert', label: 'Alert', icon: AlertTriangle, color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-700', borderColor: 'border-amber-300' }
];

const getTypeConfig = (type) => ANNOUNCEMENT_TYPES.find(t => t.value === type) || ANNOUNCEMENT_TYPES[0];

const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-IN', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const isCurrentlyActive = (announcement) => {
  if (!announcement.is_active) return false;
  const now = new Date();
  const start = new Date(announcement.start_datetime);
  const end = new Date(announcement.end_datetime);
  return now >= start && now <= end;
};

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    text: '',
    type: 'offer',
    start_datetime: '',
    end_datetime: '',
    is_active: true
  });

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/announcements?include_inactive=true`);
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleOpenModal = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormData({
        text: announcement.text,
        type: announcement.type,
        start_datetime: announcement.start_datetime.slice(0, 16), // Format for datetime-local
        end_datetime: announcement.end_datetime.slice(0, 16),
        is_active: announcement.is_active
      });
    } else {
      setEditingAnnouncement(null);
      // Default to starting now and ending in 7 days
      const now = new Date();
      const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      setFormData({
        text: '',
        type: 'offer',
        start_datetime: now.toISOString().slice(0, 16),
        end_datetime: weekLater.toISOString().slice(0, 16),
        is_active: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingAnnouncement(null);
    setFormData({
      text: '',
      type: 'offer',
      start_datetime: '',
      end_datetime: '',
      is_active: true
    });
  };

  const handleSave = async () => {
    if (!formData.text.trim()) {
      toast.error('Announcement text is required');
      return;
    }
    if (!formData.start_datetime || !formData.end_datetime) {
      toast.error('Start and end date/time are required');
      return;
    }
    if (new Date(formData.end_datetime) <= new Date(formData.start_datetime)) {
      toast.error('End date must be after start date');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        start_datetime: new Date(formData.start_datetime).toISOString(),
        end_datetime: new Date(formData.end_datetime).toISOString()
      };

      if (editingAnnouncement) {
        await axios.put(`${API}/announcements/${editingAnnouncement.id}`, payload);
        toast.success('Announcement updated successfully');
      } else {
        await axios.post(`${API}/announcements`, payload);
        toast.success('Announcement created successfully');
      }
      handleCloseModal();
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      toast.error(error.response?.data?.detail || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await axios.put(`${API}/announcements/${announcement.id}`, {
        is_active: !announcement.is_active
      });
      toast.success(`Announcement ${announcement.is_active ? 'deactivated' : 'activated'}`);
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to toggle announcement:', error);
      toast.error('Failed to update announcement');
    }
  };

  const handleDelete = async (announcement) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      await axios.delete(`${API}/announcements/${announcement.id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="announcements-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight flex items-center gap-3">
              <Megaphone className="w-8 h-8 text-primary" />
              Announcements
            </h1>
            <p className="text-slate-600 mt-1">
              Manage top bar announcements for the customer portal
            </p>
          </div>
          <Button 
            onClick={() => handleOpenModal()} 
            className="bg-primary hover:bg-primary/90"
            data-testid="create-announcement-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Announcement
          </Button>
        </div>

        {/* Info Card */}
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-3">
            <Megaphone className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">How it works</p>
              <p className="mt-1">Announcements appear in a rotating carousel at the top of the customer portal. 
                Only announcements that are <strong>active</strong> and within their <strong>scheduled date/time range</strong> will be shown.</p>
            </div>
          </div>
        </Card>

        {/* Announcements List */}
        {announcements.length === 0 ? (
          <Card className="p-12 text-center border-slate-200">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No announcements yet</p>
            <Button 
              onClick={() => handleOpenModal()} 
              variant="outline" 
              className="mt-4"
            >
              Create your first announcement
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {announcements.map((announcement) => {
              const typeConfig = getTypeConfig(announcement.type);
              const TypeIcon = typeConfig.icon;
              const isLive = isCurrentlyActive(announcement);
              
              return (
                <Card 
                  key={announcement.id} 
                  className={`p-4 border-l-4 ${typeConfig.borderColor} ${!announcement.is_active ? 'opacity-60' : ''}`}
                  data-testid={`announcement-${announcement.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${typeConfig.bgColor}`}>
                      <TypeIcon className={`w-6 h-6 ${typeConfig.textColor}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${typeConfig.bgColor} ${typeConfig.textColor}`}>
                          {typeConfig.label}
                        </span>
                        {isLive && (
                          <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            LIVE
                          </span>
                        )}
                        {!announcement.is_active && (
                          <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                            INACTIVE
                          </span>
                        )}
                      </div>
                      
                      <p className="text-lg font-medium text-slate-900 mb-2">{announcement.text}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDateTime(announcement.start_datetime)}</span>
                        </div>
                        <span className="text-slate-300">→</span>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatDateTime(announcement.end_datetime)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={announcement.is_active}
                        onCheckedChange={() => handleToggleActive(announcement)}
                        data-testid={`toggle-${announcement.id}`}
                      />
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleOpenModal(announcement)}
                        data-testid={`edit-${announcement.id}`}
                      >
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(announcement)}
                        data-testid={`delete-${announcement.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Type Selection */}
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                <SelectTrigger data-testid="announcement-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ANNOUNCEMENT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon className={`w-4 h-4 ${type.textColor}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Announcement Text */}
            <div className="space-y-2">
              <Label htmlFor="text">Announcement Text</Label>
              <Input
                id="text"
                value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})}
                placeholder="e.g., Use code SUMMER20 for 20% off!"
                maxLength={100}
                data-testid="announcement-text-input"
              />
              <p className="text-xs text-slate-500">{formData.text.length}/100 characters</p>
            </div>
            
            {/* Start DateTime */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start">Start Date & Time</Label>
                <Input
                  id="start"
                  type="datetime-local"
                  value={formData.start_datetime}
                  onChange={(e) => setFormData({...formData, start_datetime: e.target.value})}
                  data-testid="announcement-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end">End Date & Time</Label>
                <Input
                  id="end"
                  type="datetime-local"
                  value={formData.end_datetime}
                  onChange={(e) => setFormData({...formData, end_datetime: e.target.value})}
                  data-testid="announcement-end-input"
                />
              </div>
            </div>
            
            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Active</p>
                <p className="text-sm text-slate-500">Show this announcement when within date range</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                data-testid="announcement-active-switch"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseModal} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? 'Saving...' : editingAnnouncement ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
